import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreator from "@core-ui/ContextServices/ApiUrlCreator";
import type ContextService from "@core-ui/ContextServices/ContextService";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import { useValidateSource } from "@ext/git/actions/Source/logic/useValidateSource";
import type SourceData from "@ext/storage/logic/SourceDataProvider/model/SourceData";
import {
	createContext,
	type Dispatch,
	type ReactElement,
	type SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";

export const SourceDataContext = createContext<SourceData[]>(undefined);

class SourceDataService implements ContextService {
	private _setSourceDataContext: Dispatch<SetStateAction<SourceData[]>>;
	private _reload: () => Promise<void>;

	Init({ children }: { children: ReactElement }): ReactElement {
		const [sourceDatas, setSourceDatas] = useState<SourceData[]>([]);
		this._setSourceDataContext = setSourceDatas;

		const apiUrlCreator = ApiUrlCreator.value;
		const currentWorkspace = PageDataContextService.value.workspace.current;
		const validateSource = useValidateSource();
		const lastLoadId = useRef(0);

		// Only the latest load may write: a slower earlier one would bring back sources that were replaced meanwhile.
		const load = useCallback(async () => {
			const loadId = ++lastLoadId.current;
			const res = await FetchService.fetch<SourceData[]>(apiUrlCreator.getSourceData());
			if (!res.ok || loadId !== lastLoadId.current) return;
			const sourceDatas = await res.json();
			setSourceDatas(sourceDatas);
			await sourceDatas.mapAsync((sd) => validateSource(sd, sourceDatas));
			if (loadId === lastLoadId.current) setSourceDatas([...sourceDatas]);
		}, [apiUrlCreator, validateSource]);
		this._reload = load;

		useEffect(() => {
			void load();
		}, [currentWorkspace]);

		return <SourceDataContext.Provider value={sourceDatas}>{children}</SourceDataContext.Provider>;
	}

	Provider({ children, value }: { children: ReactElement; value: SourceData[] }): ReactElement {
		return <SourceDataContext.Provider value={value}>{children}</SourceDataContext.Provider>;
	}

	reload(): Promise<void> {
		return this._reload();
	}

	get value(): SourceData[] {
		return useContext(SourceDataContext);
	}

	set value(value: SourceData[]) {
		this._setSourceDataContext(value);
	}
}

export default new SourceDataService();
